/**
 * Node Utilities
 *
 * Shared utilities for flow editor node components to eliminate code duplication.
 */

import type {
  FlowNodeData,
  FlowAction,
  MessageActionConfig,
  QuickRepliesActionConfig,
  CollectInputActionConfig,
  HandoffActionConfig,
  DelayActionConfig,
  HttpRequestActionConfig,
  SetVariableActionConfig,
  GotoNodeActionConfig,
  AiNodeActionConfig,
} from "../flow-editor.types";

// ============================================================================
// Delete Handler
// ============================================================================

/**
 * Creates an onDelete handler for node components.
 * Eliminates the repeated pattern: data.onDelete ? () => data.onDelete?.(id) : undefined
 */
export const getNodeDeleteHandler = (
  data: FlowNodeData,
  id: string
): (() => void) | undefined =>
  data.onDelete ? () => data.onDelete?.(id) : undefined;

// ============================================================================
// Action Config Extraction
// ============================================================================

/**
 * Safely extracts the first action from node data.
 */
export const getFirstAction = (data: FlowNodeData): FlowAction | undefined =>
  data.actions[0];

/**
 * Type-safe config extraction for message nodes.
 */
export const getMessageConfig = (data: FlowNodeData): MessageActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "send_message") {
    return action.config;
  }
  return { message: "" };
};

/**
 * Type-safe config extraction for quick replies nodes.
 */
export const getQuickRepliesConfig = (
  data: FlowNodeData
): QuickRepliesActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "send_quick_replies") {
    return action.config;
  }
  return { message: "", replies: [] };
};

/**
 * Type-safe config extraction for collect input nodes.
 */
export const getCollectInputConfig = (
  data: FlowNodeData
): CollectInputActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "collect_input") {
    return action.config;
  }
  return { prompt: "", inputName: "" };
};

/**
 * Type-safe config extraction for handoff nodes.
 */
export const getHandoffConfig = (data: FlowNodeData): HandoffActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "handoff") {
    return action.config;
  }
  return { reason: "" };
};

/**
 * Type-safe config extraction for delay nodes.
 */
export const getDelayConfig = (data: FlowNodeData): DelayActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "delay") {
    return action.config;
  }
  return { delayAmount: 1, delayUnit: "days" };
};

/**
 * Type-safe config extraction for HTTP request nodes.
 */
export const getHttpRequestConfig = (
  data: FlowNodeData
): HttpRequestActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "http_request") {
    return action.config;
  }
  return { method: "GET", url: "" };
};

/**
 * Type-safe config extraction for set variable nodes.
 */
export const getSetVariableConfig = (
  data: FlowNodeData
): SetVariableActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "set_variable") {
    return action.config;
  }
  return { variableName: "", value: "" };
};

/**
 * Type-safe config extraction for goto nodes.
 */
export const getGotoConfig = (data: FlowNodeData): GotoNodeActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "goto_node") {
    return action.config;
  }
  return { targetNodeId: "" };
};

/**
 * Type-safe config extraction for AI nodes.
 */
export const getAiNodeConfig = (data: FlowNodeData): AiNodeActionConfig => {
  const action = getFirstAction(data);
  if (action?.type === "ai_node") {
    return action.config;
  }
  return { operation: "generate_response" };
};

// ============================================================================
// Styling Constants
// ============================================================================

/**
 * Shared icon size class for consistency across all nodes.
 */
export const NODE_ICON_SIZE = "w-4 h-4";

/**
 * Common node styling configurations.
 * Colors match the original node component implementations.
 */
export const NODE_STYLES = {
  message: {
    iconColor: "bg-blue-500/20 text-blue-500",
    borderColor: "ring-blue-500",
  },
  quickReplies: {
    iconColor: "bg-blue-500/20 text-blue-500",
    borderColor: "ring-blue-500",
  },
  collectInput: {
    iconColor: "bg-purple-500/20 text-purple-500",
    borderColor: "ring-purple-500",
  },
  condition: {
    iconColor: "bg-orange-500/20 text-orange-500",
    borderColor: "ring-orange-500",
  },
  aiResponse: {
    iconColor: "bg-cyan-500/20 text-cyan-500",
    borderColor: "ring-cyan-500",
  },
  handoff: {
    iconColor: "bg-red-500/20 text-red-500",
    borderColor: "ring-red-500",
  },
  delay: {
    iconColor: "bg-amber-500/20 text-amber-500",
    borderColor: "ring-amber-500",
  },
  entry: {
    iconColor: "bg-green-500/20 text-green-500",
    borderColor: "ring-green-500",
  },
  httpRequest: {
    iconColor: "bg-indigo-500/20 text-indigo-500",
    borderColor: "ring-indigo-500",
  },
  setVariable: {
    iconColor: "bg-teal-500/20 text-teal-500",
    borderColor: "ring-teal-500",
  },
  goto: {
    iconColor: "bg-pink-500/20 text-pink-500",
    borderColor: "ring-pink-500",
  },
} as const;
